import { FileText, Phone } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
  type Column,
} from '@/components/ui';
import {
  useImagingActions,
  useImagingToReview,
  useLabActions,
  useOutsideActions,
  useRecentOutsideReports,
  useResultsToReview,
} from '@/features/orders/api';
import {
  RESULT_FLAG_LABEL,
  REVIEW_LABEL,
  type ImagingReferral,
  type LabOrder,
  type LabResultFlag,
  type LabTestLine,
  type OutsideReport,
  type OutsideRow,
} from '@/features/orders/types';
import type { PatientSummary } from '@/features/patients/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import styles from '@/pages/queue/queue.module.css';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-GB') : '—';

interface Row {
  order: LabOrder;
  test: LabTestLine;
}

const flagBadge = (f: LabResultFlag | null | undefined) =>
  f && f !== 'normal' ? (
    <Badge tone={f === 'critical' ? 'danger' : 'warning'}>
      {RESULT_FLAG_LABEL[f]}
    </Badge>
  ) : (
    <Badge>Normal</Badge>
  );

function useFail() {
  const toast = useToast();
  return (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
}

/**
 * Admin v2 Doctor "Results to review" (artifacts #85–87): every test the lab
 * routed to me, with flag and status. Release each to the patient's app, keep
 * a critical one held until the patient has been called, read imaging reports
 * and outside lab reports.
 */
export function ResultsToReviewPage() {
  const { data, isLoading, error, refetch } = useResultsToReview();
  const [open, setOpen] = useState<LabOrder | null>(null);
  const [imaging, setImaging] = useState<ImagingReferral | null>(null);
  const [outside, setOutside] = useState(false);
  const rows: Row[] = (data ?? []).flatMap((order) =>
    order.tests
      .filter((t) => t.review !== 'released')
      .map((test) => ({ order, test })),
  );

  const columns: Column<Row>[] = [
    {
      key: 'patient',
      header: 'Patient',
      render: (r) => (
        <>
          <p className={styles.name}>{r.order.patient.fullName}</p>
          <p className={styles.sub + ' mono'}>{r.order.number}</p>
          {/* RL-1841 (D14): results left by a doctor who has gone. */}
          {r.order.takeover && (
            <p className={styles.sub}>
              Ordered by {r.order.orderedBy?.fullName ?? 'a doctor'} (no longer
              at the clinic)
            </p>
          )}
        </>
      ),
    },
    { key: 'test', header: 'Test', render: (r) => r.test.testName },
    {
      key: 'value',
      header: 'Result',
      render: (r) =>
        r.test.result ? (
          <>
            <strong>
              {r.test.result.value}
              {r.test.result.unit ? ` ${r.test.result.unit}` : ''}
            </strong>
            {r.test.result.referenceRange && (
              <span className={styles.sub}>
                {' '}
                ({r.test.result.referenceRange})
              </span>
            )}
          </>
        ) : (
          '—'
        ),
    },
    {
      key: 'flag',
      header: 'Flag',
      render: (r) => flagBadge(r.test.result?.flag),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge tone={r.test.review === 'held' ? 'danger' : 'info'}>
          {REVIEW_LABEL[r.test.review ?? 'to_review']}
        </Badge>
      ),
    },
    { key: 'in', header: 'In', render: (r) => when(r.order.resultsReadyAt) },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (r) => (
        <Button size="sm" onClick={() => setOpen(r.order)}>
          Review
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <Card title="Results to review" flush>
        <DataTable
          caption="Results to review"
          columns={columns}
          rows={data ? rows : undefined}
          rowKey={(r) => r.test.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={
            <EmptyState
              title="Nothing to review"
              body="Results for tests you ordered appear here when the lab routes them to you, with results left by a doctor who is no longer at the clinic."
            />
          }
        />
      </Card>
      <ImagingReports onOpen={setImaging} />
      <OutsideReports onRead={() => setOutside(true)} />
      {open && (
        <ReviewDialog
          orderId={open.id}
          fallback={open}
          onClose={() => setOpen(null)}
        />
      )}
      {imaging && (
        <ImagingReviewDialog
          referral={imaging}
          onClose={() => setImaging(null)}
        />
      )}
      {outside && <OutsideReportDialog onClose={() => setOutside(false)} />}
    </PageContainer>
  );
}

function ReviewDialog({
  orderId,
  fallback,
  onClose,
}: {
  orderId: string;
  fallback: LabOrder;
  onClose: () => void;
}) {
  const { data } = useResultsToReview();
  const o = data?.find((x) => x.id === orderId) ?? fallback;
  const a = useLabActions();
  const toast = useToast();
  const fail = useFail();
  const [comment, setComment] = useState('');
  const [calling, setCalling] = useState<LabTestLine | null>(null);
  const open = o.tests.filter((t) => t.review !== 'released');
  const criticalUncalled = open.some(
    (t) => t.result?.flag === 'critical' && !t.callNote,
  );
  return (
    <Dialog
      open
      wide
      title={`Review · ${o.patient.fullName}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Later</Button>
          <Button
            variant="primary"
            disabled={criticalUncalled || open.length === 0}
            loading={a.review.isPending}
            onClick={() =>
              a.review
                .mutateAsync({ id: o.id, comment: comment.trim() || undefined })
                .then(() => {
                  toast.show({
                    tone: 'success',
                    title: 'All released to the app',
                  });
                  onClose();
                })
                .catch(fail)
            }
          >
            Release all
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <p className={styles.sub}>
          <span className="mono">{o.number}</span> · ordered {when(o.orderedAt)}
          {o.clinicalNote ? ` · ${o.clinicalNote}` : ''}
        </p>
        <table className={styles.billTable} aria-label="Tests to review">
          <tbody>
            {o.tests.map((t) => (
              <tr key={t.id}>
                <td>
                  <strong>{t.testName}</strong>
                  <br />
                  {t.result
                    ? `${t.result.value}${t.result.unit ? ` ${t.result.unit}` : ''}${t.result.referenceRange ? ` (range ${t.result.referenceRange})` : ''}`
                    : '—'}{' '}
                  {flagBadge(t.result?.flag)}
                  {t.callNote && (
                    <span className={styles.sub}>
                      <br />
                      Call: {t.callNote}
                    </span>
                  )}
                </td>
                <td>
                  {t.review === 'released' ? (
                    <Badge tone="success">Released</Badge>
                  ) : (
                    <div className={styles.badges}>
                      {t.review === 'held' && (
                        <Badge tone="danger">Held · call patient</Badge>
                      )}
                      {t.result?.flag === 'critical' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Phone size={14} />}
                          onClick={() => setCalling(t)}
                        >
                          Critical: call
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          loading={a.release.isPending}
                          onClick={() =>
                            void a.release
                              .mutateAsync({
                                id: o.id,
                                itemId: t.id,
                                comment: comment.trim() || undefined,
                              })
                              .catch(fail)
                          }
                        >
                          Release to app
                        </Button>
                      )}
                      {t.review !== 'held' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void a.hold
                              .mutateAsync({ id: o.id, itemId: t.id })
                              .catch(fail)
                          }
                        >
                          Keep held
                        </Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Field label="Doctor's comment (optional)">
          <Input
            value={comment}
            onChange={(ev) => setComment(ev.target.value)}
          />
        </Field>
        {criticalUncalled && (
          <p className={styles.warnNote}>
            A critical result stays held until the patient has been called.
          </p>
        )}
        <Link className={styles.linkButton} to={`/patients/${o.patient.id}`}>
          Open full record
        </Link>
      </div>
      {calling && (
        <CriticalDialog
          order={o}
          test={calling}
          onClose={() => setCalling(null)}
        />
      )}
    </Dialog>
  );
}

/** Critical result (artifact #86): call note, then Keep held or Release to app. */
function CriticalDialog({
  order: o,
  test: t,
  onClose,
}: {
  order: LabOrder;
  test: LabTestLine;
  onClose: () => void;
}) {
  const a = useLabActions();
  const fail = useFail();
  const toast = useToast();
  const [note, setNote] = useState(t.callNote ?? '');
  return (
    <Dialog
      open
      variant="modal"
      title={`Critical result · ${t.testName}`}
      onClose={onClose}
      footer={
        <>
          <Button
            loading={a.hold.isPending}
            onClick={() =>
              a.hold
                .mutateAsync({
                  id: o.id,
                  itemId: t.id,
                  callNote: note.trim() || undefined,
                })
                .then(onClose)
                .catch(fail)
            }
          >
            Keep held
          </Button>
          <Button
            variant="primary"
            disabled={note.trim().length < 5}
            loading={a.release.isPending}
            onClick={() =>
              a.release
                .mutateAsync({ id: o.id, itemId: t.id, callNote: note.trim() })
                .then(() => {
                  toast.show({ tone: 'success', title: 'Released to the app' });
                  onClose();
                })
                .catch(fail)
            }
          >
            Release to app
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <p>
          <strong>
            {t.result?.value}
            {t.result?.unit ? ` ${t.result.unit}` : ''}
          </strong>{' '}
          {t.result?.referenceRange ? `(range ${t.result.referenceRange})` : ''}{' '}
          <Badge tone="danger">Critical</Badge>
        </p>
        <p className={styles.sub}>
          {o.patient.fullName}. Call the patient before the result reaches the
          app.
        </p>
        <Field label="Call note" hint="Who you spoke to, when, and the advice.">
          <textarea
            className={styles.textarea}
            rows={3}
            value={note}
            onChange={(ev) => setNote(ev.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function ImagingReports({ onOpen }: { onOpen: (r: ImagingReferral) => void }) {
  const { data } = useImagingToReview();
  if (!data?.length) return null;
  return (
    <Card title="Imaging reports in">
      <ul className={styles.addenda}>
        {data.map((r) => (
          <li key={r.documentId}>
            <strong>{r.patient.fullName}</strong> · {r.exam} · {r.referredTo} ·
            report {when(r.reportAt)}{' '}
            <Button size="sm" onClick={() => onOpen(r)}>
              Read
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ImagingReviewDialog({
  referral: r,
  onClose,
}: {
  referral: ImagingReferral;
  onClose: () => void;
}) {
  const a = useImagingActions();
  const fail = useFail();
  const [comment, setComment] = useState('');
  return (
    <Dialog
      open
      title={`${r.exam ?? 'Imaging'} · ${r.patient.fullName}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Later</Button>
          <Button
            variant="primary"
            loading={a.review.isPending}
            onClick={() =>
              a.review
                .mutateAsync({
                  id: r.documentId,
                  comment: comment.trim() || undefined,
                })
                .then(onClose)
                .catch(fail)
            }
          >
            Mark read
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <p className={styles.sub}>
          {r.referredTo} · {r.reason ?? ''}
        </p>
        <p style={{ whiteSpace: 'pre-wrap' }}>{r.reportText}</p>
        <Field label="Comment (optional)">
          <Input
            value={comment}
            onChange={(ev) => setComment(ev.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function OutsideReports({ onRead }: { onRead: () => void }) {
  const { data } = useRecentOutsideReports();
  return (
    <Card
      title="Outside lab reports"
      actions={
        <Button size="sm" icon={<FileText size={14} />} onClick={onRead}>
          Read an outside report
        </Button>
      }
    >
      {!data?.length ? (
        <p className={styles.sub}>
          Results from another lab, checked before they are saved.
        </p>
      ) : (
        <ul className={styles.addenda}>
          {data.map((r: OutsideReport) => (
            <li key={r.id}>
              <strong>{r.patient?.fullName}</strong> ·{' '}
              {r.source ?? 'Outside lab'} · {r.reportDate ?? when(r.enteredAt)}{' '}
              · {r.results.map((x) => `${x.test} ${x.value}`).join(', ')}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const FLAGS = [
  { value: '', label: '—' },
  ...(Object.keys(RESULT_FLAG_LABEL) as LabResultFlag[]).map((f) => ({
    value: f,
    label: RESULT_FLAG_LABEL[f],
  })),
];

/** "Outside lab report" (artifact #87): read, check each row, save. */
function OutsideReportDialog({ onClose }: { onClose: () => void }) {
  const o = useOutsideActions();
  const fail = useFail();
  const toast = useToast();
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [source, setSource] = useState('');
  const [date, setDate] = useState('');
  const [text, setText] = useState('');
  const [rows, setRows] = useState<OutsideRow[]>([]);
  const set = (i: number, patch: Partial<OutsideRow>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Dialog
      open
      wide
      title="Outside lab report"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!patient || rows.length === 0}
            loading={o.save.isPending}
            onClick={() =>
              patient &&
              o.save
                .mutateAsync({
                  patientId: patient.id,
                  source: source.trim() || undefined,
                  reportDate: date || undefined,
                  rawText: text || undefined,
                  results: rows.filter((r) => r.test.trim() && r.value.trim()),
                })
                .then(() => {
                  toast.show({
                    tone: 'success',
                    title: 'Checked results saved',
                  });
                  onClose();
                })
                .catch(fail)
            }
          >
            Save checked results
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <Field label="Patient">
          <PatientPicker value={patient} onChange={setPatient} />
        </Field>
        <div className={styles.vitalsGrid}>
          <Field label="From (lab)">
            <Input
              value={source}
              onChange={(ev) => setSource(ev.target.value)}
            />
          </Field>
          <Field label="Report date">
            <Input
              type="date"
              value={date}
              onChange={(ev) => setDate(ev.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Report text"
          hint="Paste the report; one result per line reads best."
        >
          <textarea
            className={styles.textarea}
            rows={5}
            value={text}
            onChange={(ev) => setText(ev.target.value)}
          />
        </Field>
        <div className={styles.badges}>
          <Button
            size="sm"
            disabled={text.trim().length < 3}
            loading={o.read.isPending}
            onClick={() =>
              o.read
                .mutateAsync(text)
                .then((r) => setRows(r.rows))
                .catch(fail)
            }
          >
            Read the report
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setRows([
                ...rows,
                {
                  test: '',
                  value: '',
                  unit: null,
                  referenceRange: null,
                  flag: null,
                },
              ])
            }
          >
            Add a row
          </Button>
        </div>
        <p className={styles.sub}>
          Read with a pattern reader (AI reading is not connected). Check every
          row before saving.
        </p>
        {rows.map((r, i) => (
          <div key={i} className={styles.vitalsGrid}>
            <Field label="Test">
              <Input
                value={r.test}
                onChange={(ev) => set(i, { test: ev.target.value })}
              />
            </Field>
            <Field label="Value">
              <Input
                value={r.value}
                onChange={(ev) => set(i, { value: ev.target.value })}
              />
            </Field>
            <Field label="Range">
              <Input
                value={r.referenceRange ?? ''}
                onChange={(ev) =>
                  set(i, { referenceRange: ev.target.value || null })
                }
              />
            </Field>
            <Field label="Flag">
              <Select
                options={FLAGS}
                value={r.flag ?? ''}
                onChange={(ev) =>
                  set(i, {
                    flag: (ev.target.value || null) as LabResultFlag | null,
                  })
                }
              />
            </Field>
          </div>
        ))}
      </div>
    </Dialog>
  );
}
